import React, { useState, useEffect, useRef } from 'react';

interface CurrencyInputProps {
  value: string;                          // raw numeric string from form state
  onChange: (raw: string) => void;        // called with raw numeric string
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
}

/**
 * Currency input that displays formatted Vietnamese number (e.g. 1.200.000)
 * while storing/emitting the raw numeric string for form state.
 */
const CurrencyInput: React.FC<CurrencyInputProps> = ({
  value,
  onChange,
  placeholder = '0',
  className = '',
  disabled = false,
  required = false,
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Format a raw numeric string to display string
  const formatDisplay = (raw: string): string => {
    if (!raw || raw === '') return '';
    const num = parseFloat(raw.replace(/\./g, '').replace(',', '.'));
    if (isNaN(num)) return '';
    return new Intl.NumberFormat('vi-VN').format(num);
  };

  // Sync display when external value changes (e.g. form reset / loadTrip)
  useEffect(() => {
    setDisplayValue(formatDisplay(value));
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Strip all non-digit characters except comma/dot for decimal
    const digitsOnly = raw.replace(/[^\d]/g, '');
    if (digitsOnly === '') {
      setDisplayValue('');
      onChange('');
      return;
    }
    const num = parseInt(digitsOnly, 10);
    if (isNaN(num)) return;
    setDisplayValue(new Intl.NumberFormat('vi-VN').format(num));
    onChange(String(num));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow: backspace, delete, tab, escape, enter, arrows, home, end
    const allowed = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (allowed.includes(e.key)) return;
    // Allow Ctrl/Cmd+A, C, V, X
    if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) return;
    // Block non-digit keys
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      value={displayValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      required={required}
    />
  );
};

export default CurrencyInput;
